import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HomePageNLP } from './views/HomePageNLP';
import { NLPSalesPage } from './views/NLPSalesPage';
import { ExampleResults } from './views/ExampleResults';
import { QuestionnaireContainer } from './containers/QuestionnaireContainer';
import { RegistrationPage } from './views/RegistrationPage';
import { InstructionsView } from './views/InstructionsView';
import { ResultsView } from './views/ResultsView';
import { LoginPageNLP } from './views/LoginPageNLP';
import { AdminDashboard } from './views/AdminDashboard';
import { CoachManagement } from './views/admin/CoachManagement';
import { FreeProfileCoaches } from './views/admin/FreeProfileCoaches';
import { PartnerManagement } from './views/admin/PartnerManagement';
import { AssessmentCodes } from './views/admin/AssessmentCodes';
import { Analytics } from './views/admin/Analytics';
import { AudioTranscriber } from './views/admin/AudioTranscriber';
import { EventRegistrations } from './views/admin/EventRegistrations';
import { PartnerApplications } from './views/admin/PartnerApplications';
import { LiveChatAdmin } from './views/admin/LiveChat';
import { PricingPage } from './views/PricingPage';
import { DiscoverPage } from './views/DiscoverPage';
import { CoachConversationPage } from './views/CoachConversationPage';
import { EventRegistrationPage } from './views/EventRegistrationPage';
import { EventThankYouPage } from './views/EventThankYouPage';
import { CoachingPlateauPage } from './views/CoachingPlateauPage';
import { CoachingPlateauThankYouPage } from './views/CoachingPlateauThankYouPage';
import { AssessmentProvider } from './contexts/AssessmentContext';
import { ResultsProvider } from './contexts/ResultsContext';
import { RespondentDashboard } from './views/RespondentDashboard';
import { CoachDashboard } from './views/CoachDashboard';
import { RespondentsList } from './views/coach/RespondentsList';
import { RespondentResults } from './views/coach/RespondentResults';
import { ManualAIAnalysis } from './views/coach/ManualAIAnalysis';
import { CoachAssessmentCodes } from './views/coach/AssessmentCodes';
import { PartnerDashboard } from './views/PartnerDashboard';
import { PartnerCoachManagement } from './views/partner/CoachManagement';
import { PartnerAssessmentCodes } from './views/partner/AssessmentCodes';
import { PartnerAnalytics } from './views/partner/Analytics';
import { PartnerTeamManagement } from './views/partner/TeamManagement';
import { PartnerTeamDetail } from './views/partner/TeamDetail';
import { TeamInsights } from './views/partner/TeamInsights';
import { TeamMemberResults } from './views/partner/TeamMemberResults';
import { FreeProfileReferralTools } from './views/partner/FreeProfileReferralTools';
import { PartnerSettings } from './views/partner/PartnerSettings';
import { NLPPracticeManagement } from './views/partner/NLPPracticeManagement';
import { NLPPracticePage } from './views/coach/NLPPracticePage';
import { ReframingPracticePage } from './views/coach/ReframingPracticePage';
import { NLPPracticeFeedback } from './views/coach/NLPPracticeFeedback';
import { PrivacyPage } from './views/PrivacyPage';
import { TermsPage } from './views/TermsPage';
import { DisclaimerPage } from './views/DisclaimerPage';
import { CoachRegistrationPage } from './views/CoachRegistrationPage';
import { FreeProfileSignupPage } from './views/FreeProfileSignupPage';
import { FreeProfileOnboarding } from './views/coach/FreeProfileOnboarding';
import { FreeProfileCoachDashboard } from './views/coach/FreeProfileCoachDashboard';
import { ProfileResources } from './views/coach/ProfileResources';
import { CountdownTimer } from './views/CountdownTimer';
import { WinsPage } from './views/WinsPage';
import { EnrolmentMethodPage } from './views/EnrolmentMethodPage';
import { WinsReviews } from './views/admin/WinsReviews';
import { TestimonialReviews } from './views/admin/TestimonialReviews';
import { TestimonialPage } from './views/TestimonialPage';
import { EnrolmentMethodTestimonialPage } from './views/EnrolmentMethodTestimonialPage';
import { MasterclassRegistrationPage } from './views/MasterclassRegistrationPage';
import { MastertrackSalesPage } from './views/MastertrackSalesPage';
import { MasterclassRegistrations } from './views/admin/MasterclassRegistrations';
import { BeyondTheMindRegistrationPage } from './views/BeyondTheMindRegistrationPage';
import { BeyondTheMindThankYouPage } from './views/BeyondTheMindThankYouPage';
import { BeyondTheMindRegistrations } from './views/admin/BeyondTheMindRegistrations';
import { AmbassadorPage } from './views/AmbassadorPage';
import { AmbassadorRegistrations } from './views/admin/AmbassadorRegistrations';
import { RespondentWelcomePage } from './views/RespondentWelcomePage';
import { NLPPracticeFeedbackReviews } from './views/admin/NLPPracticeFeedbackReviews';
import { NLPModelABTest } from './views/admin/NLPModelABTest';
import { AdminManagement } from './views/admin/AdminManagement';
import { TrainingTimer } from './views/TrainingTimer';
import { RevenueCalculator } from './views/RevenueCalculator';
import { AICoachSettings } from './views/coach/AICoachSettings';
import { AICoachSetupWizard } from './views/coach/AICoachSetupWizard';
import { AICoachUsage } from './views/coach/AICoachUsage';
import { CoachPathways } from './views/coach/CoachPathways';
import { PathwayBuilder } from './views/coach/PathwayBuilder';
import { PathwayView } from './views/PathwayView';
import { MyAssignedPathways } from './views/coach/MyAssignedPathways';
import { CoachPathwayView } from './views/coach/CoachPathwayView';
import { ProgressOverview } from './views/respondent/ProgressOverview';
import { GoalsView } from './views/respondent/GoalsView';
import { OutcomesView } from './components/respondent/OutcomesView';
import { HabitsView } from './views/respondent/HabitsView';
import { TasksView } from './views/respondent/TasksView';
import { CheckInsView } from './views/respondent/CheckInsView';
import { JournalView } from './views/respondent/JournalView';
import { MilestonesView } from './views/respondent/MilestonesView';
import { MyReports } from './views/respondent/MyReports';
import { RespondentAccountSettings } from './views/respondent/AccountSettings';
import { CoachAccountSettings } from './views/coach/AccountSettings';
import { CoachSharedResultsInbox } from './views/coach/SharedResultsInbox';
import { SharedResultDetail } from './views/coach/SharedResultDetail';
import { PartnerSharedResultsInbox } from './views/partner/SharedResultsInbox';
import { TransformationReports } from './views/coach/TransformationReports';
import { TransformationReportDetail } from './views/coach/TransformationReportDetail';
import { AllTransformationReports } from './views/coach/AllTransformationReports';
import { ProfitDashboard } from './views/coach/ProfitDashboard';
import { ClientProgress } from './views/coach/ClientProgress';
import { HabitInstallationOS } from './views/coach/HabitInstallationOS';
import { CoachProjects } from './views/coach/Projects';
import { CoachTeamManagement } from './views/coach/TeamManagement';
import { CoachTeamDetail } from './views/coach/TeamDetail';
import { CoachTeamInsights } from './views/coach/TeamInsights';
import { CoachTeamReports } from './views/coach/TeamReports';
import { SalesCRM } from './views/coach/SalesCRM';
import { CoachingSessions } from './views/coach/CoachingSessions';
import { SessionIntelligence } from './views/coach/SessionIntelligence';
import { LeadPageEditor } from './views/coach/LeadPageEditor';
import { LeadMagnetPage } from './views/LeadMagnetPage';
import { Day2ReplayPage } from './views/Day2ReplayPage';
import { FoundingCohortReplayPage } from './views/FoundingCohortReplayPage';
import { LivePage } from './views/LivePage';
import { SessionIntelligencePage } from './views/SessionIntelligencePage';
import { HomePageTR } from './views/tr/HomePageTR';
import { LoginPageTR } from './views/tr/LoginPageTR';
import { RespondentDashboardTR } from './views/tr/RespondentDashboardTR';
import { PathwayViewTR } from './views/tr/PathwayViewTR';
import { RegistrationPageTR } from './views/tr/RegistrationPageTR';
import { InstructionsViewTR } from './views/tr/InstructionsViewTR';
import { ResultsViewTR } from './views/tr/ResultsViewTR';
import { CoachDashboardTR } from './views/tr/CoachDashboardTR';
import { PartnerDashboardTR } from './views/tr/PartnerDashboardTR';
import { PrivacyPageTR } from './views/tr/PrivacyPageTR';
import { TermsPageTR } from './views/tr/TermsPageTR';
import { DisclaimerPageTR } from './views/tr/DisclaimerPageTR';
import { QuestionnaireContainerTR } from './containers/QuestionnaireContainerTR';
import {
  RespondentsListTR, RespondentResultsTR, ManualAIAnalysisTR,
  CoachAssessmentCodesTR, CoachProjectsTR, CoachTeamManagementTR,
  CoachTeamDetailTR, CoachTeamInsightsTR, CoachTeamReportsTR,
  AICoachSettingsTR, AICoachSetupWizardTR, AICoachUsageTR, CoachPathwaysTR,
  PathwayBuilderTR, SalesCRMTR, CoachingSessionsTR, LeadPageEditorTR,
} from './views/tr/coach/index';
import {
  PartnerCoachManagementTR, PartnerAssessmentCodesTR, PartnerAnalyticsTR,
  PartnerTeamManagementTR, PartnerTeamDetailTR, TeamInsightsTR as PartnerTeamInsightsTR,
  TeamMemberResultsTR as PartnerTeamMemberResultsTR,
} from './views/tr/partner/index';
import { HomePageFR } from './views/fr/HomePageFR';
import { LoginPageFR } from './views/fr/LoginPageFR';
import { RespondentDashboardFR } from './views/fr/RespondentDashboardFR';
import { PathwayViewFR } from './views/fr/PathwayViewFR';
import { ProgramsList } from './views/coach/ProgramsList';
import { ProgramBuilder } from './views/coach/ProgramBuilder';
import { ProgramProgress } from './views/coach/ProgramProgress';
import { MyPrograms } from './views/respondent/MyPrograms';
import { ProgramDetail as RespondentProgramDetail } from './views/respondent/ProgramDetail';
import { PartnerLayout } from './components/layout/PartnerLayout';
import { CoachLayout } from './components/layout/CoachLayout';
import { RespondentLayout } from './components/layout/RespondentLayout';
import { CoachLayoutTR } from './components/layout/tr/CoachLayoutTR';
import { PartnerLayoutTR } from './components/layout/tr/PartnerLayoutTR';
import { BrandingProvider } from './contexts/BrandingContext';

export default function App() {

  return (
    <AuthProvider>
      <BrandingProvider>
      <AssessmentProvider>
        <ResultsProvider>
          <BrowserRouter>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/home" element={<HomePageNLP />} />
              <Route path="/founding-cohort" element={<NLPSalesPage />} />
              <Route path="/example" element={<ExampleResults />} />
              <Route path="/register" element={<RegistrationPage />} />
              <Route path="/try/:slug" element={<LeadMagnetPage />} />
              <Route path="/instructions" element={<InstructionsView />} />
              <Route path="/assessment" element={<QuestionnaireContainer />} />
              <Route path="/results" element={<ResultsView />} />
              <Route path="/analysis" element={<Navigate to="/dashboard" replace />} />
              <Route path="/login" element={<LoginPageNLP />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/disclaimer" element={<DisclaimerPage />} />
              <Route path="/coach-registration" element={<CoachRegistrationPage />} />
              <Route path="/free-profile" element={<FreeProfileSignupPage />} />
              <Route path="/timer" element={<TrainingTimer />} />
              <Route path="/wins" element={<WinsPage />} />
              <Route path="/enrolmentmethod" element={<EnrolmentMethodPage />} />
              <Route path="/testimonial" element={<TestimonialPage />} />
              <Route path="/enrolmentmethod/testimonial" element={<EnrolmentMethodTestimonialPage />} />
              <Route path="/masterclass" element={<MasterclassRegistrationPage />} />
              <Route path="/beyondthemind" element={<BeyondTheMindRegistrationPage />} />
              <Route path="/beyondthemind/thank-you" element={<BeyondTheMindThankYouPage />} />
              <Route path="/mastertrack" element={<MastertrackSalesPage />} />
              <Route path="/feedback" element={<NLPPracticeFeedback />} />
              <Route path="/countdowntimer" element={<CountdownTimer />} />
              <Route path="/invitationspecial" element={<CountdownTimer />} />
              <Route path="/revenuecalculator" element={<RevenueCalculator />} />
              <Route path="/foundations-replay-day1" element={<EventRegistrationPage />} />
              <Route path="/foundations-replay-day1/thankyou" element={<EventThankYouPage />} />
              <Route path="/coaching-plateau" element={<CoachingPlateauPage />} />
              <Route path="/coaching-plateau/thankyou" element={<CoachingPlateauThankYouPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/discover/:slug" element={<DiscoverPage />} />
              <Route path="/coach-conversation" element={<CoachConversationPage />} />
              <Route path="/coach-conversation/:slug" element={<CoachConversationPage />} />
              <Route path="/day2-replay" element={<Day2ReplayPage />} />
              <Route path="/replay" element={<FoundingCohortReplayPage />} />
              <Route path="/live" element={<LivePage />} />
              <Route path="/session-intelligence" element={<SessionIntelligencePage />} />
              <Route path="/ambassador" element={<AmbassadorPage />} />
              <Route path="/welcome" element={<RespondentWelcomePage />} />

              {/* Respondent Routes (with sidebar) */}
              <Route element={<RespondentLayout />}>
                <Route path="/dashboard" element={<RespondentDashboard />} />
                <Route path="/dashboard/pathway/:assignmentId" element={<PathwayView />} />
                <Route path="/dashboard/progress" element={<ProgressOverview />} />
                <Route path="/dashboard/progress/goals" element={<GoalsView />} />
                <Route path="/dashboard/progress/outcomes" element={<OutcomesView />} />
                <Route path="/dashboard/progress/habits" element={<HabitsView />} />
                <Route path="/dashboard/progress/tasks" element={<TasksView />} />
                <Route path="/dashboard/progress/check-ins" element={<CheckInsView />} />
                <Route path="/dashboard/progress/journal" element={<JournalView />} />
                <Route path="/dashboard/progress/milestones" element={<MilestonesView />} />
                <Route path="/dashboard/proof/reports" element={<MyReports />} />
                <Route path="/dashboard/programs" element={<MyPrograms />} />
                <Route path="/dashboard/programs/:assignmentId" element={<RespondentProgramDetail />} />
                <Route path="/dashboard/account" element={<RespondentAccountSettings />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/partners" element={<PartnerManagement />} />
              <Route path="/admin/coaches" element={<CoachManagement />} />
              <Route path="/admin/free-profile-coaches" element={<FreeProfileCoaches />} />
              <Route path="/admin/codes" element={<AssessmentCodes />} />
              <Route path="/admin/analytics" element={<Analytics />} />
              <Route path="/admin/transcriber" element={<AudioTranscriber />} />
              <Route path="/admin/event-registrations" element={<EventRegistrations />} />
              <Route path="/admin/wins" element={<WinsReviews />} />
              <Route path="/admin/testimonials" element={<TestimonialReviews />} />
              <Route path="/admin/masterclass-registrations" element={<MasterclassRegistrations />} />
              <Route path="/admin/beyond-the-mind-registrations" element={<BeyondTheMindRegistrations />} />
              <Route path="/admin/nlp-practice-feedback" element={<NLPPracticeFeedbackReviews />} />
              <Route path="/admin/nlp-model-test" element={<NLPModelABTest />} />
              <Route path="/admin/nlp-model-test/practice" element={<NLPPracticePage />} />
              <Route path="/admin/admins" element={<AdminManagement />} />
              <Route path="/admin/partner-applications" element={<PartnerApplications />} />
              <Route path="/admin/live-chat" element={<LiveChatAdmin />} />
              <Route path="/admin/free-profile-referrals" element={<FreeProfileReferralTools />} />
              <Route path="/admin/ambassadors" element={<AmbassadorRegistrations />} />

              {/* Coach Routes (with sidebar) */}
              <Route element={<CoachLayout />}>
                <Route path="/coach" element={<CoachDashboard />} />
                <Route path="/coach/onboarding" element={<FreeProfileOnboarding />} />
                <Route path="/coach/profile-resources" element={<ProfileResources />} />
                <Route path="/coach/respondents" element={<RespondentsList />} />
                <Route path="/coach/results/:userId" element={<RespondentResults />} />
                <Route path="/coach/manual-analysis" element={<ManualAIAnalysis />} />
                <Route path="/coach/codes" element={<CoachAssessmentCodes />} />
                <Route path="/coach/projects" element={<CoachProjects />} />
                <Route path="/coach/teams" element={<CoachTeamManagement />} />
                <Route path="/coach/teams/:teamId" element={<CoachTeamDetail />} />
                <Route path="/coach/teams/:teamId/insights" element={<CoachTeamInsights />} />
                <Route path="/coach/reports" element={<CoachTeamReports />} />
                <Route path="/coach/ai-settings" element={<AICoachSettings />} />
                <Route path="/coach/ai-setup" element={<AICoachSetupWizard />} />
                <Route path="/coach/ai-usage" element={<AICoachUsage />} />
                <Route path="/coach/pathways" element={<CoachPathways />} />
                <Route path="/coach/pathways/new" element={<PathwayBuilder />} />
                <Route path="/coach/pathways/:id/edit" element={<PathwayBuilder />} />
                <Route path="/coach/my-pathways" element={<MyAssignedPathways />} />
                <Route path="/coach/my-pathways/:assignmentId" element={<CoachPathwayView />} />
                <Route path="/coach/programs" element={<ProgramsList creatorType="coach" basePath="/coach/programs" />} />
                <Route path="/coach/programs/new" element={<ProgramBuilder creatorType="coach" basePath="/coach/programs" />} />
                <Route path="/coach/programs/:id/edit" element={<ProgramBuilder creatorType="coach" basePath="/coach/programs" />} />
                <Route path="/coach/programs/:id/progress" element={<ProgramProgress basePath="/coach/programs" />} />
                <Route path="/coach/crm" element={<SalesCRM />} />
                <Route path="/coach/sessions" element={<CoachingSessions />} />
                <Route path="/coach/session-intelligence" element={<SessionIntelligence />} />
                <Route path="/coach/lead-page" element={<LeadPageEditor />} />
                <Route path="/coach/proof/:userId/reports" element={<TransformationReports />} />
                <Route path="/coach/proof/:userId/reports/:reportId" element={<TransformationReportDetail />} />
                <Route path="/coach/transformation-reports" element={<AllTransformationReports />} />
                <Route path="/coach/progress" element={<ClientProgress />} />
                <Route path="/coach/habit-os" element={<HabitInstallationOS />} />
                <Route path="/coach/profit" element={<ProfitDashboard />} />
                <Route path="/coach/account" element={<CoachAccountSettings />} />
                <Route path="/coach/shared-results" element={<CoachSharedResultsInbox />} />
                <Route path="/coach/shared-results/:shareId" element={<SharedResultDetail />} />
                <Route path="/coach/nlp-practice" element={<NLPPracticePage />} />
                <Route path="/coach/nlp-practice/feedback" element={<NLPPracticeFeedback />} />
                <Route path="/coach/reframing-practice" element={<ReframingPracticePage />} />
              </Route>

              {/* Turkish Routes */}
              <Route path="/tr" element={<HomePageTR />} />
              <Route path="/tr/login" element={<LoginPageTR />} />
              <Route path="/tr/register" element={<RegistrationPageTR />} />
              <Route path="/tr/instructions" element={<InstructionsViewTR />} />
              <Route path="/tr/assessment" element={<QuestionnaireContainerTR />} />
              <Route path="/tr/results" element={<ResultsViewTR />} />
              <Route path="/tr/privacy" element={<PrivacyPageTR />} />
              <Route path="/tr/terms" element={<TermsPageTR />} />
              <Route path="/tr/disclaimer" element={<DisclaimerPageTR />} />

              {/* Turkish Respondent Routes */}
              <Route path="/tr/dashboard" element={<RespondentDashboardTR />} />
              <Route path="/tr/dashboard/pathway/:assignmentId" element={<PathwayViewTR />} />

              {/* Turkish Coach Routes (with sidebar) */}
              <Route element={<CoachLayoutTR />}>
                <Route path="/tr/coach" element={<CoachDashboardTR />} />
                <Route path="/tr/coach/respondents" element={<RespondentsListTR />} />
                <Route path="/tr/coach/results/:userId" element={<RespondentResultsTR />} />
                <Route path="/tr/coach/manual-analysis" element={<ManualAIAnalysisTR />} />
                <Route path="/tr/coach/codes" element={<CoachAssessmentCodesTR />} />
                <Route path="/tr/coach/projects" element={<CoachProjectsTR />} />
                <Route path="/tr/coach/teams" element={<CoachTeamManagementTR />} />
                <Route path="/tr/coach/teams/:teamId" element={<CoachTeamDetailTR />} />
                <Route path="/tr/coach/teams/:teamId/insights" element={<CoachTeamInsightsTR />} />
                <Route path="/tr/coach/reports" element={<CoachTeamReportsTR />} />
                <Route path="/tr/coach/ai-settings" element={<AICoachSettingsTR />} />
                <Route path="/tr/coach/ai-setup" element={<AICoachSetupWizardTR />} />
                <Route path="/tr/coach/ai-usage" element={<AICoachUsageTR />} />
                <Route path="/tr/coach/pathways" element={<CoachPathwaysTR />} />
                <Route path="/tr/coach/pathways/new" element={<PathwayBuilderTR />} />
                <Route path="/tr/coach/pathways/:id/edit" element={<PathwayBuilderTR />} />
                <Route path="/tr/coach/crm" element={<SalesCRMTR />} />
                <Route path="/tr/coach/sessions" element={<CoachingSessionsTR />} />
                <Route path="/tr/coach/lead-page" element={<LeadPageEditorTR />} />
                <Route path="/tr/coach/progress" element={<ClientProgress />} />
                <Route path="/tr/coach/habit-os" element={<HabitInstallationOS />} />
                <Route path="/tr/coach/transformation-reports" element={<AllTransformationReports />} />
              </Route>

              {/* Turkish Partner Routes (with sidebar) */}
              <Route element={<PartnerLayoutTR />}>
                <Route path="/tr/partner" element={<PartnerDashboardTR />} />
                <Route path="/tr/partner/coaches" element={<PartnerCoachManagementTR />} />
                <Route path="/tr/partner/codes" element={<PartnerAssessmentCodesTR />} />
                <Route path="/tr/partner/analytics" element={<PartnerAnalyticsTR />} />
                <Route path="/tr/partner/teams" element={<PartnerTeamManagementTR />} />
                <Route path="/tr/partner/teams/:teamId" element={<PartnerTeamDetailTR />} />
                <Route path="/tr/partner/teams/:teamId/insights" element={<PartnerTeamInsightsTR />} />
                <Route path="/tr/partner/teams/:teamId/results/:userId" element={<PartnerTeamMemberResultsTR />} />
              </Route>

              {/* French Routes */}
              <Route path="/fr" element={<HomePageFR />} />
              <Route path="/fr/login" element={<LoginPageFR />} />
              <Route path="/fr/dashboard" element={<RespondentDashboardFR />} />
              <Route path="/fr/dashboard/pathway/:assignmentId" element={<PathwayViewFR />} />

              {/* Partner Routes (with sidebar) */}
              <Route element={<PartnerLayout />}>
                <Route path="/partner" element={<PartnerDashboard />} />
                <Route path="/partner/coaches" element={<PartnerCoachManagement />} />
                <Route path="/partner/codes" element={<PartnerAssessmentCodes />} />
                <Route path="/partner/analytics" element={<PartnerAnalytics />} />
                <Route path="/partner/teams" element={<PartnerTeamManagement />} />
                <Route path="/partner/teams/:teamId" element={<PartnerTeamDetail />} />
                <Route path="/partner/teams/:teamId/insights" element={<TeamInsights />} />
                <Route path="/partner/teams/:teamId/results/:userId" element={<TeamMemberResults />} />
              <Route path="/partner/free-profile-referrals" element={<FreeProfileReferralTools />} />
              <Route path="/partner/programs" element={<ProgramsList creatorType="partner" basePath="/partner/programs" />} />
              <Route path="/partner/programs/new" element={<ProgramBuilder creatorType="partner" basePath="/partner/programs" />} />
              <Route path="/partner/programs/:id/edit" element={<ProgramBuilder creatorType="partner" basePath="/partner/programs" />} />
              <Route path="/partner/programs/:id/progress" element={<ProgramProgress basePath="/partner/programs" />} />
              <Route path="/partner/pathways" element={<CoachPathways creatorType="partner" basePath="/partner/pathways" />} />
              <Route path="/partner/pathways/new" element={<PathwayBuilder creatorType="partner" basePath="/partner/pathways" />} />
              <Route path="/partner/pathways/:id/edit" element={<PathwayBuilder creatorType="partner" basePath="/partner/pathways" />} />
              <Route path="/partner/settings" element={<PartnerSettings />} />
              <Route path="/partner/shared-results" element={<PartnerSharedResultsInbox />} />
              <Route path="/partner/nlp-practice" element={<NLPPracticeManagement />} />
              </Route>
            </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </ResultsProvider>
      </AssessmentProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}
